<script setup lang="ts">
import KgrAskForPushPermission from '@shared/components/KgrAskForPushPermission.vue';
import KgrLoadingContainer from '@shared/components/KgrLoadingContainer.vue';
import KgrModal from '@shared/components/KgrModal.vue';
import KgrOfflineMessage from '@shared/components/KgrOfflineMessage.vue';
import KgrReAuthLayer from '@shared/components/KgrReAuthLayer.vue';
import OtaUpdateOverlay from '@shared/components/OtaUpdateOverlay.vue';
import { initApp, isAppInitializing } from '@shared/composables/useAppInit';
import { initCapgo } from '@shared/composables/useCapgo';
import { initDeepLinks } from '@shared/composables/useDeepLinks';
import { usePush } from '@shared/composables/usePush';
import { useAppStateStore } from '@shared/stores/app-state';
import { useUserStore } from '@shared/stores/user';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import AppHeader from './components/core/AppHeader.vue';
import AppTabbar from './components/core/AppTabbar.vue';
import MainMenu from './components/core/MainMenu.vue';
import UserMenu from './components/core/UserMenu.vue';
import { APP_CONFIG } from './config';

type MenuState = 'idle' | 'mainMenuOpen' | 'userMenuOpen';

const appState = useAppStateStore();
const userStore = useUserStore();
const router = useRouter();
const push = usePush();

const menuState = ref<MenuState>('idle');

router.afterEach(() => {
    menuState.value = 'idle';
});

const user = computed(() => userStore.currentUser);

function openForegroundMessage() {
    const data = push.foregroundMessage.value;
    if (!data) {
        return;
    }
    // Angular ForegroundPushMessageComponent.onOpenClick: immer ueber den Institution-Redirect, damit
    // eine Nachricht fuer ein anderes Konto dieses Geraets zuerst den Account wechselt.
    push.openMessage(router, data);
    push.foregroundMessage.value = null;
}

onMounted(async () => {
    await initApp();
    // A white-label client ships its own Firebase project (see window.d.ts `firebase`).
    push.init(window.kgrDynamicConfig.firebase ?? APP_CONFIG.firebase, router);
    if (appState.isNative) {
        initDeepLinks(router);
        initCapgo();
    }
});
</script>

<template>
    <OtaUpdateOverlay />

    <RouterView name="popup" />

    <KgrModal
        :open="!!push.foregroundMessage.value"
        max-width="560px"
        @close="push.foregroundMessage.value = null"
    >
        <template v-if="push.foregroundMessage.value">
            <div class="kgr-modal__header kgr-region kgr-region--accent">
                <h2 class="kgr-headline kgr-headline--h3">
                    {{ push.foregroundMessage.value.title }}
                </h2>
            </div>
            <div class="kgr-modal__content kgr-region kgr-text kgr-text--preserveLineBreaks">
                {{ push.foregroundMessage.value.body }}
            </div>
            <div class="kgr-postfix kgr-modal__footer kgr-region kgr-postfix--between">
                <button
                    type="button"
                    class="kgr-button kgr-col"
                    @click="push.foregroundMessage.value = null"
                >
                    {{ $t('Schließen') }}
                </button>
                <button
                    v-if="push.foregroundMessage.value.appRoute"
                    type="button"
                    class="kgr-button"
                    @click="openForegroundMessage()"
                >
                    {{ $t('Öffnen') }}
                </button>
            </div>
        </template>
    </KgrModal>
    <div
        class="kgr-offCanvas kgr-offCanvas--safeArea"
        :class="{
            'is-open-left': menuState === 'mainMenuOpen',
            'is-open-right': menuState === 'userMenuOpen',
        }"
    >
        <div class="offCanvasMenu mainMenu kgr-offCanvas__off kgr-offCanvas__off--fixed kgr-offCanvas__off--left">
            <MainMenu />
        </div>
        <div class="offCanvasMenu userMenu kgr-offCanvas__off kgr-offCanvas__off--fixed kgr-offCanvas__off--right">
            <!-- Always rendered (also when logged out): the UserMenu then shows only the
                 language switch (Angular `@else` branch), which is white-label relevant. -->
            <UserMenu :user="user" />
        </div>

        <div class="kgr-offCanvas__on">
            <div class="kgr-pageLayout kgr-pageLayout--sticky">
                <div class="kgr-pageLayout__header">
                    <AppHeader
                        :menu-state="menuState"
                        @toggle-main-menu="menuState = menuState === 'mainMenuOpen' ? 'idle' : 'mainMenuOpen'"
                        @toggle-user-menu="menuState = menuState === 'userMenuOpen' ? 'idle' : 'userMenuOpen'"
                    />
                </div>
                <div class="kgr-pageLayout__main">
                    <!-- Blocking loader until user + organization are loaded (Angular [kgrLoading] on
                         the main content). The fallthrough class keeps it the same element as Angular,
                         not an extra wrapper. -->
                    <KgrLoadingContainer
                        class="kgr-pageLayout__mainContent"
                        :loading="isAppInitializing"
                        :blocking="true"
                    >
                        <KgrOfflineMessage />
                        <RouterView :key="String($route.params.locale)" />
                    </KgrLoadingContainer>
                </div>
                <AppTabbar
                    v-if="user && appState.isScreenSmallAndDown"
                    class="kgr-pageLayout__footer"
                />
            </div>
        </div>

        <div
            class="kgr-offCanvas__backdrop kgr-clickable"
            @click="menuState = 'idle'"
        ></div>
    </div>

    <KgrAskForPushPermission />
    <KgrReAuthLayer />
</template>

<style lang="scss">
.offCanvasMenu {
    min-width: 250px;
    max-width: calc(100vw - 55px);
    height: 100%;
    background-color: var(--kgr-color-surface);
}
</style>
